import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SusuGame.css';

/* Find Susu — a remake of a freshman-year p5.js sketch.
   Nine bushes. Susu hops between the ones still standing; poop hides
   behind one of them. Clear a bush: Susu behind it wins, poop loses. */

const COUNT = 9;

/* Where each bush sits: centre x, ground line y (both % of the scene)
   and scale. An even 3×3 — same size, straight rows, equal gaps. */
const SPOTS = [
  [20, 60, 0.8], [50, 60, 0.8], [80, 60, 0.8],
  [20, 77, 0.8], [50, 77, 0.8], [80, 77, 0.8],
  [20, 94, 0.8], [50, 94, 0.8], [80, 94, 0.8],
];
const HOP_MS = 480; // how long Susu stays behind one bush

const pick = (list) => list[Math.floor(Math.random() * list.length)];

/* Tiny synth so the game needs no sound files */
function useSounds() {
  const ctxRef = useRef(null);
  return useCallback((notes, type = 'sine') => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = ctxRef.current || (ctxRef.current = new Ctx());
      let t = ctx.currentTime;
      notes.forEach(([freq, dur]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.12, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + dur + 0.02);
        t += dur * 0.8;
      });
    } catch (e) {
      /* sound is a nicety; never break the game over it */
    }
  }, []);
}

function newGame() {
  const poop = Math.floor(Math.random() * COUNT);
  const others = [...Array(COUNT).keys()].filter((i) => i !== poop);
  return { standing: Array(COUNT).fill(true), poop, susu: pick(others), score: 0, result: null };
}

/* Print-style bush, to sit inside the riso/gouache park painting: three
   flat greens (dark underneath, mid, light on the top-left where the
   light falls), lumpy edges and a speckled grain. The wobble and grain
   come from one shared SVG filter, defined once in <SusuFilters />. */
const BUSH_SHAPES = [
  {
    fill: '#3f5f45',
    clumps: [[28, 56, 20], [50, 49, 24], [76, 47, 24], [98, 57, 18], [14, 64, 12], [108, 66, 10]],
    base: [10, 58, 100, 18],
  },
  { fill: '#7f9c52', clumps: [[38, 46, 18], [62, 39, 20], [86, 47, 15]] },
  { fill: '#a5c25d', clumps: [[34, 39, 12], [52, 31, 14], [66, 33, 8]] },
];

/* a few pale flecks, like the light dabs in the painting's trees */
const FLECKS = [[46, 30, 2.4], [78, 50, 2]];

function SusuFilters() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <filter id="susu-print" x="-10%" y="-10%" width="120%" height="120%">
          {/* wobbly, hand-cut edges */}
          <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="3" seed="4" result="warp" />
          <feDisplacementMap in="SourceGraphic" in2="warp" scale="10" xChannelSelector="R" yChannelSelector="G" result="wobble" />
          {/* paper grain: speckle the ink out in tiny spots */}
          <feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="1" seed="9" result="noise" />
          <feColorMatrix
            in="noise"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 2.05"
            result="grain"
          />
          <feComposite in="wobble" in2="grain" operator="in" />
        </filter>
      </defs>
    </svg>
  );
}

function Bush() {
  return (
    <svg viewBox="0 0 120 80" className="susu-bush-art" aria-hidden="true">
      <g filter="url(#susu-print)">
        {BUSH_SHAPES.map((shape) => (
          <g key={shape.fill} fill={shape.fill}>
            {shape.clumps.map(([cx, cy, r]) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
            ))}
            {shape.base && <rect x={shape.base[0]} y={shape.base[1]} width={shape.base[2]} height={shape.base[3]} />}
          </g>
        ))}
        <g fill="#cfe08a">
          {FLECKS.map(([cx, cy, r]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
          ))}
        </g>
      </g>
    </svg>
  );
}

export default function SusuGame() {
  const [game, setGame] = useState(newGame);
  const play = useSounds();
  const over = game.result !== null;

  // Susu hops to another standing, poop-free bush
  useEffect(() => {
    if (over) return undefined;
    const id = setInterval(() => {
      setGame((g) => {
        const spots = g.standing
          .map((up, i) => (up && i !== g.poop ? i : null))
          .filter((i) => i !== null);
        if (spots.length < 2) return g;
        const next = pick(spots.filter((i) => i !== g.susu));
        return { ...g, susu: next };
      });
    }, HOP_MS);
    return () => clearInterval(id);
  }, [over]);

  const clear = (i) => {
    if (over || !game.standing[i]) return;
    const standing = game.standing.slice();
    standing[i] = false;
    let result = null;
    if (i === game.susu) result = 'win';
    else if (i === game.poop) result = 'lose';

    if (result === 'win') play([[523, 0.12], [659, 0.12], [784, 0.12], [1047, 0.3]], 'triangle');
    else if (result === 'lose') play([[330, 0.18], [262, 0.18], [196, 0.4]], 'sawtooth');
    else play([[880, 0.12]]);

    setGame({ ...game, standing, score: game.score + 1, result });
  };

  return (
    <div
      className={`susu-game${over ? ` susu-game--${game.result}` : ''}`}
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/art-susu-park.webp)` }}
    >
      <SusuFilters />
      <div className="susu-board">
        {SPOTS.map(([x, y, scale], i) => (
          <div
            key={i}
            className="susu-spot"
            style={{ left: `${x}%`, top: `${y}%`, width: `${30 * scale}%`, zIndex: Math.round(y) }}
          >
            <span className="susu-shadow" aria-hidden="true" />
            {i === game.poop && <span className="susu-poop" aria-hidden="true">💩</span>}
            {i === game.susu && (
              <img
                className="susu-dog"
                src={`${process.env.PUBLIC_URL}/images/art-susu.png`}
                alt=""
                draggable={false}
              />
            )}
            <button
              type="button"
              className={`susu-bush${game.standing[i] ? '' : ' susu-bush--gone'}`}
              onClick={() => clear(i)}
              disabled={over || !game.standing[i]}
              aria-label={`Bush ${i + 1}`}
            >
              <Bush />
            </button>
          </div>
        ))}
      </div>

      <div className="susu-status" aria-live="polite">
        {game.result === 'win' && <span>You found Susu!</span>}
        {game.result === 'lose' && <span>Ew, you stepped in poop.</span>}
        {!over && <span>Find Susu · {game.score} cleared</span>}
        {over && (
          <button type="button" className="susu-again" onClick={() => setGame(newGame())}>
            Play again
          </button>
        )}
      </div>
    </div>
  );
}
